import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TextField, Button, MenuItem, Box, Typography, Drawer, Stack, List, Dialog, DialogTitle, DialogActions, ListItem, IconButton, Card, CardContent, CardActions, Alert, Collapse } from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';

const RecipeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  // Determine basePath based on current route (admin or coach)
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isCoach = user?.role === 'coach';
  const basePath = isAdminRoute ? '/admin' : '/coach';

  const [recipe, setRecipe] = useState({
    name: '',
    mealType: '',
    category: '',
    preparationTime: 0,
    cookingTime: 0,
    ingredients: [],
    instructions: '',
    macros: {
      calories: 0,
      carbohydrates: 0,
      proteins: 0,
      fats: 0,
    },
    intolerances: [],
  });
  const [ingredientList, setIngredientList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const INTOLERANCE_OPTIONS = [
    { value: 'gluten', label: 'Gluten' },
    { value: 'lactose', label: 'Lactose' },
    { value: 'nuts', label: 'Noix' },
    { value: 'shellfish', label: 'Fruits de mer' },
    { value: 'soy', label: 'Soja' },
    { value: 'eggs', label: 'Œufs' },
    { value: 'fish', label: 'Poisson' },
    { value: 'pork', label: 'Porc' },
    { value: 'beef', label: 'Bœuf' },
    { value: 'chicken', label: 'Poulet' },
    { value: 'turkey', label: 'Dinde' },
    { value: 'corn', label: 'Maïs' },
    { value: 'sesame', label: 'Sésame' },
    { value: 'mustard', label: 'Moutarde' },
    { value: 'peanuts', label: 'Arachides' },
    { value: 'citrus', label: 'Agrumes' },
    { value: 'mushrooms', label: 'Champignons' },
    { value: 'legumes', label: 'Légumineuses' },
    { value: 'other', label: 'Autre' },
  ];  

  useEffect(() => {
    fetch('/ciqual.json')
      .then(response => response.json())
      .then(data => setIngredientList(data));
  }, []);

    // Fonction utilitaire pour parser les valeurs nutritionnelles
    const parseNutrientValue = (value) => {
        if (typeof value === 'string') {
          value = value.replace(',', '.'); // Remplacer la virgule par un point
          if (value.includes('<') || value === 'traces' || value === '-' || value === '') {
            return 0; // Remplacer les valeurs non numériques ou manquantes par zéro
          }
        }
        return parseFloat(value) || 0;
      };      

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRecipe({ ...recipe, [name]: value });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
    setSearchTerm(''); // Réinitialise la recherche quand on ouvre le drawer
  };

  const handleIngredientSelect = (ingredient) => {
    setSelectedIngredient(ingredient);
    setSearchTerm(ingredient.alim_nom_fr); // Définir le terme de recherche sur l'ingrédient sélectionné
    setQuantity(''); // Réinitialiser la quantité après la sélection d'un nouvel ingrédient
  };
  
  const filteredIngredients = ingredientList.filter(ingredient =>
    ingredient.alim_nom_fr.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Calcule les macros pour une liste d'ingrédients donnée
  const calculateMacrosForIngredients = (ingredients) => {
    let totalCarbs = 0;
    let totalProteins = 0;
    let totalFats = 0;

    ingredients.forEach(ingredient => {
      const ciqualData = ingredientList.find(item => item.alim_code === ingredient.ciqual_code);
      if (ciqualData) {
        const quantityInGrams = ingredient.quantity;
        totalCarbs += (parseNutrientValue(ciqualData["Glucides (g/100 g)"]) * quantityInGrams) / 100;
        totalProteins += (parseNutrientValue(ciqualData["Protéines, N x facteur de Jones (g/100 g)"]) * quantityInGrams) / 100;
        totalFats += (parseNutrientValue(ciqualData["Lipides (g/100 g)"]) * quantityInGrams) / 100;
      }
    });

    const recalculatedCalories = (totalCarbs * 4) + (totalProteins * 4) + (totalFats * 9);

    return {
      calories: recalculatedCalories,
      carbohydrates: totalCarbs,
      proteins: totalProteins,
      fats: totalFats
    };
  };

  const addIngredient = () => {
    if (selectedIngredient && quantity) {
      const newIngredients = [
        ...recipe.ingredients,
        { name: selectedIngredient.alim_nom_fr, ciqual_code: selectedIngredient.alim_code, quantity: parseFloat(quantity) }
      ];
      const newMacros = calculateMacrosForIngredients(newIngredients);
      setRecipe({
        ...recipe,
        ingredients: newIngredients,
        macros: newMacros
      });
      setSelectedIngredient(null);
      setQuantity('');
      setDrawerOpen(false);
    }
  };

  const deleteIngredient = (indexToDelete) => {
    const newIngredients = recipe.ingredients.filter((_, index) => index !== indexToDelete);
    const newMacros = calculateMacrosForIngredients(newIngredients);
    setRecipe({
      ...recipe,
      ingredients: newIngredients,
      macros: newMacros
    });
  };

  const handleSubmit = async () => {
    // Frontend validation
    const missingFields = [];
    if (!recipe.name?.trim()) missingFields.push('Nom de la recette');
    if (!recipe.mealType) missingFields.push('Type de repas');
    if (!recipe.ingredients || recipe.ingredients.length === 0) missingFields.push('Au moins un ingredient');
    if (!recipe.instructions?.trim()) missingFields.push('Instructions');

    if (missingFields.length > 0) {
      setErrorMessage(`Champs requis manquants : ${missingFields.join(', ')}`);
      setShowAlert(true);
      return;
    }

    try {
      if (isAdminRoute) {
        // Admin: create via admin global recipes API
        // Only send required/allowed fields
        const recipeData = {
          name: recipe.name.trim(),
          description: recipe.description || '',
          mealType: recipe.mealType,
          category: Array.isArray(recipe.category) ? recipe.category : (recipe.category ? [recipe.category] : []),
          intolerances: recipe.intolerances || [],
          preparationTime: Number(recipe.preparationTime) || 0,
          cookingTime: Number(recipe.cookingTime) || 0,
          ingredients: recipe.ingredients.map(ing => ({
            name: ing.name,
            ciqual_code: String(ing.ciqual_code),
            quantity: ing.quantity,
            unit: ing.unit || 'g'
          })),
          instructions: recipe.instructions.trim(),
          macros: recipe.macros,
        };
        await api.post('/admin/recipes', recipeData);
      } else {
        // Coach: create via multi-tenant API (stored in tenant DB)
        const recipeData = {
          name: recipe.name.trim(),
          description: recipe.description || '',
          mealType: recipe.mealType,
          category: Array.isArray(recipe.category) ? recipe.category : (recipe.category ? [recipe.category] : []),
          intolerances: recipe.intolerances || [],
          preparationTime: Number(recipe.preparationTime) || 0,
          cookingTime: Number(recipe.cookingTime) || 0,
          ingredients: recipe.ingredients.map(ing => ({
            name: ing.name,
            ciqual_code: String(ing.ciqual_code),
            quantity: ing.quantity,
            unit: ing.unit || 'g'
          })),
          instructions: recipe.instructions.trim(),
          macros: recipe.macros,
        };
        await api.post('/coach/recipes', recipeData);
      }

      setSuccessModalOpen(true);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la recette:', error);
      setErrorMessage(`Erreur lors de la sauvegarde de la recette : ${error.message}`);
      setShowAlert(true);
    }
  };
  
  
  return (
    <>
      <Helmet>
        <title>Creer recette - {isAdminRoute ? 'Admin' : 'Coach'} Yoco</title>
      </Helmet>

      <Box sx={{ maxWidth: '880px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(`${basePath}/recipes`)}>
            Retour
          </Button>
        </Stack>

        <Card>
          <CardContent>
            <Typography sx={{ mb: 3 }} variant="h1">Creer une recette</Typography>

      <TextField
        label="Nom de la recette"
        name="name"
        value={recipe.name}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Type de repas"
        name="mealType"
        value={recipe.mealType}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
        select
      >
        <MenuItem value="breakfast">Petit-déjeuner</MenuItem>
        <MenuItem value="lunch">Déjeuner</MenuItem>
        <MenuItem value="dinner">Dîner</MenuItem>
        <MenuItem value="snack">Encas</MenuItem>
      </TextField>
      <TextField
        label="Catégorie"
        name="category"
        value={recipe.category}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
        select
      >
        <MenuItem value="vegetarian">Vegetarien</MenuItem>
        <MenuItem value="vegan">Vegan</MenuItem>
        <MenuItem value="gluten-free">Sans gluten</MenuItem>
        <MenuItem value="dairy-free">Sans produits laitiers</MenuItem>
        <MenuItem value="paleo">Paléo</MenuItem>
        <MenuItem value="keto">Kéto</MenuItem>
        <MenuItem value="other">Autre</MenuItem>
      </TextField>
      <TextField
        label="Temps de préparation (min)"
        name="preparationTime"
        value={recipe.preparationTime}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Temps de cuisson (min)"
        name="cookingTime"
        value={recipe.cookingTime}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
      />

      {/* Bouton pour ouvrir le drawer de sélection des ingrédients */}
      <Button  sx={{ width:'100%', boxShadow:'none' }} variant="contained" color="secondary" onClick={toggleDrawer(true)} startIcon={<AddIcon />}>
        Ajouter un ingredient
      </Button>

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h5">Ingrédients</Typography>
          <TextField
            label="Rechercher"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            sx={{ mb: 2, mt:2 }}
            InputProps={{
              endAdornment: <SearchIcon />
            }}
          />
          <List>
            {filteredIngredients.map(ingredient => (
              <ListItem
                button
                key={ingredient.alim_code}
                onClick={() => handleIngredientSelect(ingredient)}
                selected={selectedIngredient?.alim_code === ingredient.alim_code}
              >
                {ingredient.alim_nom_fr}
                {selectedIngredient?.alim_code === ingredient.alim_code && (
                  <CheckCircleIcon sx={{ ml: 2, color: 'green' }} />
                )}
              </ListItem>
            ))}
          </List>
          <TextField
            label="Quantité (g)"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={!selectedIngredient} // Désactiver tant qu'aucun ingrédient n'est sélectionné
          />
          <Button
            variant="contained"
            onClick={addIngredient}
            sx={{ mt: 2, width:'100%', boxShadow:'none' }}
            disabled={!selectedIngredient || !quantity} // Désactiver tant qu'aucun ingrédient ou quantité n'est fourni
          >
            Ajouter
          </Button>
        </Box>
      </Drawer>

      {/* Liste des ingredients ajoutes */}
      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {recipe.ingredients.map((ingredient, index) => (
          <Card key={index} sx={{ width: 250 }}>
            <CardContent sx={{ pb: 1 }}>
              <Typography sx={{ mb: 1, lineHeight: 1.3 }} variant="h3">{ingredient.name}</Typography>
              <Typography variant="body2">Quantite : {ingredient.quantity}g</Typography>
              <Typography variant="body2" color="text.secondary">CIQUAL : {ingredient.ciqual_code}</Typography>
            </CardContent>
            <CardActions sx={{ pt: 0 }}>
              <IconButton size="small" aria-label="delete" onClick={() => deleteIngredient(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </CardActions>
          </Card>
        ))}
      </Box>

        {/* Affichage des macros calculés */}
        {recipe.macros.calories >= 0 && (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Calories : {recipe.macros.calories.toFixed(2)} kcal</Typography>
            <Typography variant="body1">Glucides : {recipe.macros.carbohydrates.toFixed(2)} g</Typography>
            <Typography variant="body1">Protéines : {recipe.macros.proteins.toFixed(2)} g</Typography>
            <Typography variant="body1">Lipides : {recipe.macros.fats.toFixed(2)} g</Typography>
        </Box>
        )}



      <TextField
        label="Instructions"
        name="instructions"
        value={recipe.instructions}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={4}
        sx={{ mt: 4, mb:2 }}
      />

      <TextField
        select
        label="Intolérances"
        name="intolerances"
        value={recipe.intolerances}
        onChange={(e) =>
          setRecipe({ ...recipe, intolerances: e.target.value })
        }
        fullWidth
        sx={{ mb: 2 }}
        SelectProps={{
          multiple: true, // Permet la sélection multiple
          renderValue: (selected) => selected.map((intol) => INTOLERANCE_OPTIONS.find((opt) => opt.value === intol)?.label || intol).join(', '),
        }}
      >
        {INTOLERANCE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>


      {/* Bouton pour sauvegarder la recette */}
      <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ mt: 2, width:'100%', boxShadow:'none'  }}>
        Sauvegarder
      </Button>

      <Collapse in={showAlert}>
        <Alert
          severity="error"
          onClose={() => setShowAlert(false)}
          sx={{ mb: 2, mt:2 }}
        >
          {errorMessage}
        </Alert>
      </Collapse>

      </CardContent>
      </Card>

        <Dialog open={successModalOpen} onClose={() => setSuccessModalOpen(false)}>
          <DialogTitle>Recette creee avec succes</DialogTitle>
          <DialogActions>
            <Button onClick={() => navigate(`${basePath}/recipes`)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default RecipeForm;
